'use client';

import {
  useEffect,
  useId,
  useLayoutEffect,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { getRouteReloadOwners } from '@/lib/reload-route-coverage';
import { getSessionDraftNativeReloadSafety } from '@/lib/session-draft-native-safety';
import {
  reloadBlockerRegistry,
  type ReloadSafety,
} from '@/lib/reload-blockers';
import {
  readNativeReloadSafetyBridge,
  startNativeReloadSafetyReporting,
} from '@/lib/native-reload-safety';

const serverSafety = (): ReloadSafety => 'unknown';

export function useReloadSafety() {
  return useSyncExternalStore(
    reloadBlockerRegistry.subscribe,
    reloadBlockerRegistry.getReloadSafety,
    serverSafety
  );
}

export function useReloadBlocker(kind: string, state: ReloadSafety) {
  const pathname = usePathname();
  const instance = useId();
  const id = `${kind}:${instance}`;
  useLayoutEffect(
    () =>
      reloadBlockerRegistry.registerReloadBlocker(
        id,
        'unknown',
        pathname ?? undefined
      ),
    [id, pathname]
  );
  useLayoutEffect(() => {
    reloadBlockerRegistry.setReloadBlocker(id, state);
  }, [id, pathname, state]);
}

export function ReloadSafetyProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  useLayoutEffect(
    () =>
      reloadBlockerRegistry.registerReloadBlocker(
        'session-draft-storage',
        'safe',
        undefined,
        () => getSessionDraftNativeReloadSafety(window.sessionStorage)
      ),
    []
  );
  useLayoutEffect(() => {
    reloadBlockerRegistry.setRouteCoverage(
      pathname ?? '/',
      pathname ? getRouteReloadOwners(pathname) : null
    );
  }, [pathname]);
  useEffect(() => {
    const bridge = readNativeReloadSafetyBridge(
      'imaiDesktop' in window ? window.imaiDesktop : null
    );
    if (!bridge) return;
    const reporting = startNativeReloadSafetyReporting(
      bridge,
      reloadBlockerRegistry
    );
    window.addEventListener('pagehide', reporting.suspend);
    window.addEventListener('pageshow', reporting.resume);
    return () => {
      window.removeEventListener('pagehide', reporting.suspend);
      window.removeEventListener('pageshow', reporting.resume);
      reporting.dispose();
    };
  }, []);
  return children;
}
